/** Motivos de pieza Courier (valor estable; etiqueta vía i18n). */
export const COURIER_MOTIVO_OPTIONS = [
  "sample",
  "leftWithClient",
  "swatch",
  "color",
  "card",
  "other",
];

export function courierMotivoI18nKey(value = "") {
  const v = String(value || "")
    .trim()
    .toLowerCase();
  if (v === "leftwithclient" || v === "left_with_client") {
    return "courierControl.reasonLeftWithClient";
  }
  if (COURIER_MOTIVO_OPTIONS.includes(v)) {
    return `courierControl.reason${v.charAt(0).toUpperCase()}${v.slice(1)}`;
  }
  return null;
}

/** Se queda / se deja en cliente (reunión u opción manual). */
export function isCourierLeftWithClientMotivo(motivo = "") {
  const m = String(motivo || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "");
  if (!m) return false;
  if (m === "leftwithclient") return true;
  if (m.includes("queda en cliente") || m.includes("deja en cliente")) {
    return true;
  }
  return false;
}

export function isCourierLeftWithClientEnvio(envio = {}) {
  if (isCourierLeftWithClientMotivo(envio?.Notas)) return true;
  const lines = Array.isArray(envio?.contenidoLineas)
    ? envio.contenidoLineas
    : Array.isArray(envio?.lineas)
      ? envio.lineas
      : [];
  return lines.some((l) => isCourierLeftWithClientMotivo(l?.Motivo));
}

/** Compañías courier habituales para el select de Courier Control (A–Z; Other al final). */
export const COURIER_COMPANIES = [
  "Aramex",
  "Correos Express",
  "DHL",
  "FedEx",
  "SF Express",
  "TNT",
  "UPS",
  "Other",
];

/**
 * Tipos de embalaje del Excel Couriers_Control → Parameters → Packing_Type.
 * Valor = texto del Excel (se guarda en TipoEmbalaje). Orden alfabético.
 */
export const COURIER_PACKING_OPTIONS = [
  "Box",
  "Box 1/2",
  "Box 2/2",
  "En Mano",
  "Envelope",
  "Fedex_Medium",
  "Fedex_Small",
  "MALETA",
  "TNT_Bag_1",
  "TNT_Bag_2",
];
const PACKING_I18N_KEYS = {
  Envelope: "packingEnvelope",
  Fedex_Small: "packingFedexSmall",
  Fedex_Medium: "packingFedexMedium",
  TNT_Bag_1: "packingTntBag1",
  TNT_Bag_2: "packingTntBag2",
  Box: "packingBox",
  "Box 1/2": "packingBoxHalf1",
  "Box 2/2": "packingBoxHalf2",
  MALETA: "packingSuitcase",
  "En Mano": "packingByHand",
};

export function courierPackingI18nKey(value = "") {
  const raw = String(value || "").trim();
  if (PACKING_I18N_KEYS[raw]) {
    return `courierControl.${PACKING_I18N_KEYS[raw]}`;
  }
  const lower = raw.toLowerCase();
  if (lower === "box" || lower === "envelope") {
    const canon = COURIER_PACKING_OPTIONS.find(
      (o) => o.toLowerCase() === lower,
    );
    if (canon) return `courierControl.${PACKING_I18N_KEYS[canon]}`;
  }
  return null;
}
export const ORIGIN_SDS = "SDS";
export const ORIGIN_CHINA_SUPPLIER = "China / Supplier";
export const STAGE_CHINA_ORIGIN = "ZHEJIANG C-STAGE IMP & EXP CO., LTD";

export function parseProveedoresPermitidos(raw) {
  if (!raw) return [];
  if (Array.isArray(raw))
    return raw.map((x) => String(x).trim()).filter(Boolean);
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.map((x) => String(x).trim()).filter(Boolean)
      : [];
  } catch {
    return [];
  }
}

/** Usuario de fábrica (China etc.): tiene ProveedoresPermitidos. */
export function isChinaCourierUser(user = {}) {
  return parseProveedoresPermitidos(user.ProveedoresPermitidos).length > 0;
}

function normProv(value = "") {
  return String(value).trim().toLowerCase().replace(/\s+/g, " ");
}

/** Stage China (Zhejiang C-Stage): origen fijo. */
export function isStageChinaUser(user = {}) {
  const allowed = parseProveedoresPermitidos(user.ProveedoresPermitidos).map(
    normProv,
  );
  if (!allowed.length) return false;
  return allowed.some(
    (a) =>
      a === normProv(STAGE_CHINA_ORIGIN) ||
      a.includes("c-stage") ||
      a === "121",
  );
}

/** Tipos de destino en el diálogo: China sin Proveedor. */
export function destinationTiposForCourierUser(user = {}) {
  if (isChinaCourierUser(user)) {
    return ["INTERNO", "CLIENTE"];
  }
  return ["CLIENTE", "PROVEEDOR", "INTERNO"];
}

/**
 * Opciones de origen según usuario:
 * - Stage China → solo Zhejiang C-Stage
 * - China (otros) → sus proveedores permitidos
 * - España → SDS + China genérico + todos los proveedores
 */
export function buildOriginOptionsForUser(
  proveedores = [],
  user = {},
  extras = {},
) {
  const all = Array.isArray(proveedores) ? proveedores : [];
  if (isStageChinaUser(user)) {
    return [STAGE_CHINA_ORIGIN];
  }
  if (isChinaCourierUser(user)) {
    const allowed = new Set(
      parseProveedoresPermitidos(user.ProveedoresPermitidos).map(normProv),
    );
    const names = all
      .map((p) => String(p?.N_PROVEEDOR || "").trim())
      .filter(Boolean)
      .filter(
        (name) =>
          allowed.has(normProv(name)) ||
          allowed.has(String(all.find((p) => p.N_PROVEEDOR === name)?.CODPROV)),
      );
    // también si el permitido es el nombre exacto
    const fromAllowed = parseProveedoresPermitidos(user.ProveedoresPermitidos);
    const merged = sortLabelsAlpha(
      [...new Set([...names, ...fromAllowed])].filter(Boolean),
    );
    return merged.length ? merged : sortLabelsAlpha(fromAllowed);
  }
  return buildOriginOptions(all, collectClientOriginLabels(extras));
}

/** Usuarios con ProveedoresPermitidos (fábrica China/Turquía…). */
export function filterChinaUsers(users = []) {
  return (Array.isArray(users) ? users : []).filter(
    (u) =>
      String(u.ACTIVO || "S").toUpperCase() !== "N" && isChinaCourierUser(u),
  );
}

function isActiveUser(user = {}) {
  return String(user.ACTIVO || "S").toUpperCase() !== "N";
}

function proveedorTokensForUser(user = {}, proveedores = []) {
  const allowed = parseProveedoresPermitidos(user.ProveedoresPermitidos);
  const tokens = new Set(allowed.map(normProv).filter(Boolean));
  for (const raw of allowed) {
    const t = normProv(raw);
    const match = (Array.isArray(proveedores) ? proveedores : []).find((p) => {
      const name = normProv(p?.N_PROVEEDOR);
      const code = String(p?.CODPROV ?? "").trim();
      return (
        t === name ||
        t === code ||
        (name && (name.includes(t) || t.includes(name)))
      );
    });
    if (match) {
      if (match.N_PROVEEDOR) tokens.add(normProv(match.N_PROVEEDOR));
      if (match.CODPROV != null) tokens.add(String(match.CODPROV).trim());
      if (match.PAIS) tokens.add(normProv(match.PAIS));
    }
  }
  return [...tokens];
}

/**
 * Región para Solicitado por: china | turkey | spain.
 * Fábrica sin pista clara → factory (solo colegas con proveedores).
 */
export function courierRequestedByRegion(user = {}, proveedores = []) {
  if (!isChinaCourierUser(user)) return "spain";
  const text = proveedorTokensForUser(user, proveedores).join(" ");
  if (/turkey|turqu[ií]a|\btur\b|tekstil|istanbul|izmir/.test(text)) {
    return "turkey";
  }
  if (
    /china|zhejiang|c-stage|suzhou|guangzhou|shanghai|hangzhou|ningbo|beijing|121/.test(
      text,
    )
  ) {
    return "china";
  }
  return "factory";
}

/**
 * Solicitado por:
 * - China → solo usuarios China
 * - Turquía → solo usuarios Turquía
 * - España (sin ProveedoresPermitidos) → todos los activos
 * Nunca cae al listado completo si eres fábrica.
 */
export function filterRequestedByUsers(
  users = [],
  currentUser = {},
  proveedores = [],
) {
  const list = Array.isArray(users) ? users : [];
  const active = list.filter(isActiveUser);
  const myRegion = courierRequestedByRegion(currentUser, proveedores);

  if (myRegion === "spain") return active;

  let pool = active.filter((u) => {
    const r = courierRequestedByRegion(u, proveedores);
    if (myRegion === "factory") {
      if (r === "spain") return false;
      const mine = proveedorTokensForUser(currentUser, proveedores);
      const theirs = proveedorTokensForUser(u, proveedores);
      return mine.some((m) =>
        theirs.some((t) => t === m || t.includes(m) || m.includes(t)),
      );
    }
    return r === myRegion;
  });

  const currentId = currentUser?.id;
  if (
    currentId != null &&
    isActiveUser(currentUser) &&
    !pool.some((u) => Number(u.id) === Number(currentId))
  ) {
    pool = [currentUser, ...pool];
  }
  if (
    !pool.length &&
    isActiveUser(currentUser) &&
    isChinaCourierUser(currentUser)
  ) {
    return [currentUser];
  }
  return pool;
}

/** Orden alfabético (es/insensitive) para listas de origen/destino. */
export function sortLabelsAlpha(labels = []) {
  return [...(Array.isArray(labels) ? labels : [])].sort((a, b) =>
    String(a || "").localeCompare(String(b || ""), "es", {
      sensitivity: "base",
      numeric: true,
    }),
  );
}

export function sortCustomersByName(customers = []) {
  return [...(Array.isArray(customers) ? customers : [])].sort((a, b) =>
    String(a?.name || a?.NAME || "").localeCompare(
      String(b?.name || b?.NAME || ""),
      "es",
      { sensitivity: "base", numeric: true },
    ),
  );
}

/**
 * Filtro Autocomplete: solo opciones cuyo texto empieza por lo escrito
 * (no “contiene”). Lista ya ordenada A–Z se mantiene.
 */
export function filterOptionsStartsWith(options, state, getLabel) {
  const list = Array.isArray(options) ? options : [];
  const q = String(state?.inputValue || "")
    .trim()
    .toLowerCase();
  if (!q) return list;
  const labelOf =
    typeof getLabel === "function"
      ? getLabel
      : (opt) =>
          typeof opt === "string"
            ? opt
            : String(
                opt?.name || opt?.NAME || opt?.N_PROVEEDOR || opt?.label || "",
              );
  return list.filter((opt) =>
    String(labelOf(opt) || "")
      .trim()
      .toLowerCase()
      .startsWith(q),
  );
}

export function sortProveedoresByName(proveedores = []) {
  return [...(Array.isArray(proveedores) ? proveedores : [])].sort((a, b) =>
    String(a?.N_PROVEEDOR || "").localeCompare(
      String(b?.N_PROVEEDOR || ""),
      "es",
      { sensitivity: "base", numeric: true },
    ),
  );
}

/** Nombres de clientes TON y marcas (líneas tipo Zara Global Urban Pants). */
export function collectClientOriginLabels({
  customers = [],
  marcas = [],
} = {}) {
  const out = [];
  for (const c of Array.isArray(customers) ? customers : []) {
    const n = String(c?.name || c?.NAME || "").trim();
    if (n) out.push(n);
  }
  for (const m of Array.isArray(marcas) ? marcas : []) {
    if (String(m?.ACTIVO || "S").toUpperCase() === "N") continue;
    const n = String(m?.MARCA || m?.marca || m?.name || "").trim();
    if (n) out.push(n);
  }
  return out;
}

/**
 * Opciones de origen: SDS + China genérico + resto (proveedores/clientes/marcas) A–Z.
 */
export function buildOriginOptions(proveedores = [], extraLabels = []) {
  const names = (Array.isArray(proveedores) ? proveedores : [])
    .map((p) => String(p?.N_PROVEEDOR || "").trim())
    .filter(Boolean);
  const extras = (Array.isArray(extraLabels) ? extraLabels : [])
    .map((x) => String(x || "").trim())
    .filter(Boolean);
  const seen = new Set();
  const out = [];
  const pushUnique = (label) => {
    const key = String(label || "")
      .trim()
      .toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(String(label).trim());
  };
  // Fijos arriba; el resto ordenado alfabéticamente
  pushUnique(ORIGIN_SDS);
  pushUnique(ORIGIN_CHINA_SUPPLIER);
  for (const label of sortLabelsAlpha([...names, ...extras])) {
    pushUnique(label);
  }
  return out;
}

/**
 * País a partir del origen (China / SDS / PAIS del proveedor).
 */
export function inferCountryFromOrigin(origen, proveedores = []) {
  const o = String(origen || "").trim();
  if (!o) return "";
  if (/china/i.test(o)) return "China";
  if (/^sds$/i.test(o) || /sitges/i.test(o) || /spain/i.test(o)) {
    return "Spain";
  }
  const match = (Array.isArray(proveedores) ? proveedores : []).find(
    (p) =>
      String(p?.N_PROVEEDOR || "")
        .trim()
        .toLowerCase() === o.toLowerCase(),
  );
  const pais = String(match?.PAIS || "").trim();
  if (pais) return pais;
  if (/turkey|turqu[ií]a/i.test(o)) return "Turkey";
  return "";
}

/** Formatea dirección de ficha cliente/proveedor para Courier. */
export function formatEntityShippingAddress(entity = {}, tipo = "") {
  const t = String(tipo || "").toUpperCase();
  if (t === "PROVEEDOR" || entity?.N_PROVEEDOR != null || entity?.DIRECCION) {
    const parts = [
      String(entity?.DIRECCION || "").trim(),
      String(entity?.POBLACION || entity?.CIUDAD || entity?.city || "").trim(),
      String(entity?.PAIS || entity?.country || "").trim(),
    ].filter(Boolean);
    return parts.join(", ");
  }
  const parts = [
    String(entity?.address || entity?.ADDRESS || "").trim(),
    String(entity?.city || entity?.CITY || "").trim(),
    String(entity?.zipCode || entity?.state || entity?.ZIP || "").trim(),
    String(entity?.country || entity?.COUNTRY || "").trim(),
  ].filter(Boolean);
  return parts.join(", ");
}

/**
 * Opciones de dirección de envío: ficha completa, ciudad suelta y extras
 * (p. ej. direcciones usadas antes o Madrid / Berryboom).
 */
export function buildShippingAddressOptions(
  entity = null,
  { tipo = "CLIENTE", extraLabels = [] } = {},
) {
  const seen = new Set();
  const out = [];
  const push = (raw) => {
    const s = String(raw || "").trim();
    if (!s) return;
    const key = s.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(s);
  };
  if (entity) {
    push(formatEntityShippingAddress(entity, tipo));
    const city = String(
      entity?.city || entity?.CITY || entity?.POBLACION || entity?.CIUDAD || "",
    ).trim();
    push(city);
  }
  for (const x of Array.isArray(extraLabels) ? extraLabels : []) {
    push(x);
  }
  return out;
}

/**
 * Etiqueta de destino a guardar en Courier.Destino desde el form Samples.
 */
export function destinoLabelFromEnvioDestino(
  dest = {},
  { customers = [], proveedores = [] } = {},
) {
  const tipo = String(dest.TIPO_DESTINO || "").toUpperCase();
  if (tipo === "INTERNO") return "SDS Spain";
  if (tipo === "CLIENTE") {
    const c = customers.find(
      (row) => Number(row.id) === Number(dest.ID_CLIENTE),
    );
    return String(c?.name || c?.Name || "").trim() || "";
  }
  if (tipo === "PROVEEDOR") {
    const p = proveedores.find(
      (row) => Number(row.CODPROV) === Number(dest.CODPROV),
    );
    return String(p?.N_PROVEEDOR || "").trim() || "";
  }
  return "";
}

/**
 * Intenta reconstruir el form de destino Samples desde Destino/Contacto guardados.
 */
export function inferEnvioDestinoFromCourier(
  envio = {},
  { customers = [], proveedores = [] } = {},
) {
  const destino = String(envio.Destino || "").trim();
  const contacto = String(envio.Contacto || "").trim();
  const base = {
    TIPO_DESTINO: "",
    ID_CLIENTE: null,
    CODPROV: null,
    ID_CONTACTO: null,
    PERSONA_ENVIO: contacto,
    DIRECCION_ENVIO: String(envio.DireccionEnvio || "").trim(),
    ID_Marca: null,
  };
  if (!destino) return base;

  const lower = destino.toLowerCase();
  if (
    lower === "sds" ||
    lower.includes("sds") ||
    lower.includes("interno") ||
    lower.includes("internal")
  ) {
    return { ...base, TIPO_DESTINO: "INTERNO" };
  }

  const customer = customers.find(
    (c) =>
      String(c?.name || c?.Name || "")
        .trim()
        .toLowerCase() === lower,
  );
  if (customer) {
    return {
      ...base,
      TIPO_DESTINO: "CLIENTE",
      ID_CLIENTE: customer.id,
    };
  }

  const proveedor = proveedores.find(
    (p) =>
      String(p?.N_PROVEEDOR || "")
        .trim()
        .toLowerCase() === lower,
  );
  if (proveedor) {
    return {
      ...base,
      TIPO_DESTINO: "PROVEEDOR",
      CODPROV: proveedor.CODPROV,
    };
  }

  return base;
}

export function userDisplayName(user = {}) {
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  if (name) return name;
  return (
    String(user.name || user.username || user.email || "").trim() ||
    (user.id != null ? `#${user.id}` : "")
  );
}
